import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import { Animated, Easing, StyleSheet, View, type EasingFunction, type ImageProps } from 'react-native';
import Dimens from 'src/core/utils/Dimens';

export type CrossfadeImageSource = number | { uri: string };

export function isEqual(one: CrossfadeImageSource, two?: CrossfadeImageSource) {
	if (!two) {
		return false;
	}
	return one === two;
}

export interface CrossfadeImageProps extends Omit<ImageProps, 'source'> {
	source: CrossfadeImageSource;
	duration?: number;
	easing?: EasingFunction;
	children?: ReactNode;
}

export const usePrevious = (value: CrossfadeImageSource) => {
	const ref = useRef<CrossfadeImageSource | undefined>(undefined);

	useEffect(() => {
		ref.current = value;
	});

	return ref.current;
};

export const CrossfadeImage = ({
	style,
	source,
	duration = Dimens.animationDuration250,
	easing = Easing.ease,
	children,
	...props
}: CrossfadeImageProps) => {
	const prevSource = usePrevious(source);
	const nextSource = useRef<CrossfadeImageSource | undefined>(undefined);
	const opacity = useRef(new Animated.Value(Dimens.opacity0)).current;
	const [oldSource, setOldSource] = useState<CrossfadeImageSource>(source);
	const [newSource, setNewSource] = useState<CrossfadeImageSource>();

	useLayoutEffect(() => {
		if (prevSource && !isEqual(source, prevSource)) {
			if (!nextSource.current) {
				setNewSource(source);
			}

			nextSource.current = source;
		}
	}, [source, prevSource]);

	const handleUpdate = useCallback(() => {
		setNewSource(nextSource.current);
		opacity.setValue(Dimens.opacity0);

		if (isEqual(oldSource, nextSource.current)) {
			nextSource.current = undefined;
		}
	}, [opacity, oldSource]);

	const handleLoad = useCallback(() => {
		Animated.timing(opacity, {
			toValue: Dimens.opacity100,
			duration,
			easing,
			useNativeDriver: true,
		}).start(() => {
			if (newSource && !isEqual(oldSource, newSource)) {
				setOldSource(newSource);
			} else {
				handleUpdate();
			}
		});
	}, [opacity, oldSource, newSource, duration, easing, handleUpdate]);

	return (
		<View style={[styles.root, style]}>
			<ThermenImage {...props} source={oldSource} style={styles.image} onLoad={handleUpdate} />
			{newSource && (
				<Animated.Image
					{...props}
					fadeDuration={Dimens.animationDuration0}
					source={newSource}
					style={[styles.image, { opacity }]}
					onLoad={handleLoad}
				/>
			)}
			{children}
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		overflow: 'hidden',
	},
	image: {
		...StyleSheet.absoluteFillObject,
		width: '100%',
		height: '100%',
	},
});
